import React, { useEffect, useRef, useState } from 'react';

/**
 * TrackingTest — Module 3 (replaces Tilt Recovery)
 * Mouse-based target tracking test.
 * A circle moves in a Lissajous path for 30 seconds.
 * Player moves the mouse cursor to stay inside the target ring.
 * Records time-on-target ratio and average normalized distance.
 */
export default function TrackingTest({ trials, onComplete, icon, moduleNum = 3, totalModules = 6 }) {
  const canvasRef = useRef(null);
  const cursorRef = useRef({ x: 300, y: 200 });
  const samplesRef = useRef([]);
  const startTimeRef = useRef(null);
  const animFrameRef = useRef(null);
  const sampleIntervalRef = useRef(null);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const warmupPhase = useRef(true);
  const [warmupComplete, setWarmupComplete] = useState(false);
  // activeDurationRef: 5000 during warmup, DURATION during real test
  const activeDurationRef = useRef(5000);

  const [uiState, setUiState] = useState('instructions'); // instructions | running | done
  const [timeLeft, setTimeLeft] = useState(5);

  const config = trials[0] || {};
  const DURATION = config.duration || 30000;
  const TARGET_RADIUS = 30;
  const amplitude = config.amplitude || 0.40;

  // Progressive difficulty: starts slow, ramps to a firm-but-trackable ceiling
  const START_FREQ_X = 0.4;
  const START_FREQ_Y = 0.35;
  const END_FREQ_X = 2.0;
  const END_FREQ_Y = 1.6;

  useEffect(() => {
    document.body.style.cursor = uiState === 'running' ? 'none' : '';
    return () => { document.body.style.cursor = ''; };
  }, [uiState]);

  const getTargetPos = (elapsed, canvas) => {
    // Use activeDurationRef for the ramp calculation so warmup feels proportionally similar
    const rampDuration = activeDurationRef.current;
    const t = Math.pow(Math.min(1, elapsed / rampDuration), 1.5);
    const currentFreqX = START_FREQ_X + (END_FREQ_X - START_FREQ_X) * t;
    const currentFreqY = START_FREQ_Y + (END_FREQ_Y - START_FREQ_Y) * t;

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const ax = canvas.width * amplitude;
    const ay = canvas.height * amplitude;
    const ts = elapsed / 1000;
    return {
      x: cx + ax * Math.sin(currentFreqX * ts),
      y: cy + ay * Math.sin(currentFreqY * ts),
    };
  };

  const finishTest = () => {
    if (completedRef.current) return;
    completedRef.current = true;

    cancelAnimationFrame(animFrameRef.current);
    clearInterval(sampleIntervalRef.current);

    if (warmupPhase.current) {
      // Warmup finished — show overlay then start real test
      setWarmupComplete(true);
      setTimeout(() => {
        warmupPhase.current = false;
        activeDurationRef.current = DURATION;
        completedRef.current = false;
        samplesRef.current = [];
        cursorRef.current = { x: 400, y: 260 };
        setWarmupComplete(false);
        setTimeLeft(Math.ceil(DURATION / 1000));
        // Restart the test loop
        startTestLoop();
      }, 2000);
      return;
    }

    const samples = samplesRef.current;
    const totalSamples = samples.length || 1;
    const onTargetCount = samples.filter((s) => s.onTarget).length;
    const timeOnTarget = onTargetCount / totalSamples;
    const avgNormDistance = samples.reduce((sum, s) => sum + s.normDist, 0) / totalSamples;

    onCompleteRef.current([{
      type: 'tracking',
      rt: null,
      correct: timeOnTarget >= 0.3,
      timeOnTarget,
      avgNormDistance,
    }]);
  };

  const startTestLoop = () => {
    // Defer canvas access to after render
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      startTimeRef.current = performance.now();

      const draw = () => {
        const elapsed = performance.now() - startTimeRef.current;
        if (elapsed >= activeDurationRef.current) {
          finishTest();
          return;
        }

        ctx.fillStyle = '#0a0e1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const pos = getTargetPos(elapsed, canvas);
        const cursor = cursorRef.current;
        const dx = cursor.x - pos.x;
        const dy = cursor.y - pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const onTarget = dist <= TARGET_RADIUS;

        // Outer ring glow when on target
        if (onTarget) {
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, TARGET_RADIUS + 6, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(16,185,129,0.1)';
          ctx.fill();
        }

        // Target ring
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, TARGET_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,212,255,0.12)';
        ctx.fill();
        ctx.strokeStyle = onTarget ? '#10b981' : '#00d4ff';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Inner center dot on target
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.fill();

        // Cursor dot
        ctx.beginPath();
        ctx.arc(cursor.x, cursor.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = onTarget ? '#10b981' : '#ef4444';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.4)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        animFrameRef.current = requestAnimationFrame(draw);
      };

      // Sample cursor vs target every 100ms
      sampleIntervalRef.current = setInterval(() => {
        if (completedRef.current) return;
        const elapsed = performance.now() - startTimeRef.current;
        const c = canvasRef.current;
        if (!c) return;
        const pos = getTargetPos(elapsed, c);
        const cursor = cursorRef.current;
        const dx = cursor.x - pos.x;
        const dy = cursor.y - pos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        samplesRef.current.push({
          onTarget: dist <= TARGET_RADIUS,
          normDist: dist / TARGET_RADIUS,
        });
        setTimeLeft(Math.ceil((activeDurationRef.current - elapsed) / 1000));
      }, 100);

      animFrameRef.current = requestAnimationFrame(draw);
    }, 50);
  };

  const startTest = () => {
    completedRef.current = false;
    samplesRef.current = [];
    cursorRef.current = { x: 400, y: 260 };
    setUiState('running');
    startTestLoop();
  };

  const startWarmup = () => {
    warmupPhase.current = true;
    activeDurationRef.current = 5000;
    setTimeLeft(5);
    startTest();
  };

  // Mouse + touch move handlers
  useEffect(() => {
    if (uiState !== 'running') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const toCanvasCoords = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    };

    const handleMouseMove = (e) => {
      cursorRef.current = toCanvasCoords(e.clientX, e.clientY);
    };

    const handleTouchMove = (e) => {
      e.preventDefault(); // block page scroll during tracking
      const touch = e.touches[0];
      cursorRef.current = toCanvasCoords(touch.clientX, touch.clientY);
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      cursorRef.current = toCanvasCoords(touch.clientX, touch.clientY);
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchstart', handleTouchStart);
    };
  }, [uiState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      clearInterval(sampleIntervalRef.current);
    };
  }, []);

  // --- Render ---

  return (
    <div style={{ width: '100%' }}>
      {uiState === 'instructions' && (
        <div className="card text-center" style={{ maxWidth: 480, margin: '0 auto' }}>
          {icon && <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--accent-dim)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', margin: '0 auto 20px' }}>{icon}</div>}
          <h2 style={{ marginBottom: 8 }}>Target Tracking</h2>
          <p style={{ marginBottom: 8 }}>
            A circle will move across the screen in an unpredictable path.
            Move your mouse to keep your cursor inside the ring for as long as possible.
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>
            Input: <span className="text-accent mono">MOUSE / TOUCH DRAG</span>
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>
            30 seconds — the ring turns <span style={{ color: '#10b981' }}>green</span> when you're on target.
          </p>
          <p className="text-muted" style={{ fontSize: 12, marginBottom: 24, lineHeight: 1.6 }}>
            The target <strong style={{ color: 'var(--text-secondary)' }}>speeds up</strong> and gets more erratic as time goes on.
          </p>
          <button className="btn btn--primary" onClick={startWarmup}>
            Start
          </button>
        </div>
      )}

      {uiState === 'running' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {warmupPhase.current && !warmupComplete ? (
              <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>
                PRACTICE 5s warmup — not scored &nbsp;
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{timeLeft}s</span> remaining
              </span>
            ) : (
              <span className="text-muted" style={{ fontSize: 13 }}>
                Tracking... <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{timeLeft}s</span> remaining
              </span>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <div className="canvas-wrapper">
              <canvas
                ref={canvasRef}
                width={800}
                height={520}
                style={{ width: '100%', maxWidth: 800, background: '#0a0e1a', cursor: 'none', display: 'block' }}
              />
            </div>
            {warmupComplete && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(10,14,26,0.92)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                borderRadius: 8,
              }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981', marginBottom: 8 }}>
                  Warmup Complete!
                </div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
                  Starting real test in 2 seconds...
                </div>
              </div>
            )}
          </div>
          <p className="text-center text-muted" style={{ fontSize: 13 }}>
            Module {moduleNum} of {totalModules} — Target Tracking
          </p>
        </div>
      )}
    </div>
  );
}
